import React, { useState, useCallback } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { phoneEndpoint } from "../utils/trustCallApi";
import type { TrustCallPhone } from "../types";

interface AppScoreModalProps {
  show: boolean;
  phone: TrustCallPhone | null;
  onClose: () => void;
}

// Status color mapping matching MVC (Monitor.cshtml lines 4197-4208)
function getStatusHtml(status: string | null | undefined): { text: string; color?: string } {
  if (status === "Flagged") return { text: "Flagged", color: "#FF0000" };
  if (status === "Clean") return { text: "Clean", color: "#008000" };
  return { text: "Not Run" };
}

const AppScoreModal: React.FC<AppScoreModalProps> = ({ show, phone, onClose }) => {
  const phoneNumber = phone?.phoneNumber ?? "";
  const phoneNumberClean = phone?.phoneNumberClean || phoneNumber.replace(/\D/g, "");

  // Independent show/hide + data state for each app (matching MVC toggle behavior)
  const [nomoroboVisible, setNomoroboVisible] = useState(false);
  const [nomoroboJson, setNomoroboJson] = useState<string | null>(null);
  const [nomoroboLoading, setNomoroboLoading] = useState(false);

  const [roboKillerVisible, setRoboKillerVisible] = useState(false);
  const [roboKillerJson, setRoboKillerJson] = useState<string | null>(null);
  const [roboKillerLoading, setRoboKillerLoading] = useState(false);

  const fetchAppScoreJson = useCallback(
    async (appScoreType: string): Promise<string> => {
      const result = await callApi(
        `${phoneEndpoint(phoneNumberClean, "app-score-json")}?appScoreType=${appScoreType}`,
        HttpMethod.GET
      );
      const raw = result.json ?? result.responseJSON;
      if (!raw) return JSON.stringify(result, null, 4);
      // If the backend returned a pre-formatted string, parse and re-format
      // with 4-space indentation to match MVC output
      if (typeof raw === "string") {
        try {
          return JSON.stringify(JSON.parse(raw), null, 4);
        } catch {
          return raw;
        }
      }
      return JSON.stringify(raw, null, 4);
    },
    [phoneNumberClean]
  );

  const handleNomoroboToggle = async () => {
    if (nomoroboVisible) {
      setNomoroboVisible(false);
      return;
    }
    if (nomoroboJson === null) {
      setNomoroboLoading(true);
      try {
        const json = await fetchAppScoreJson("nomorobo");
        setNomoroboJson(json);
      } catch {
        setNomoroboJson("Error loading details");
      } finally {
        setNomoroboLoading(false);
      }
    }
    setNomoroboVisible(true);
  };

  const handleRoboKillerToggle = async () => {
    if (roboKillerVisible) {
      setRoboKillerVisible(false);
      return;
    }
    if (roboKillerJson === null) {
      setRoboKillerLoading(true);
      try {
        const json = await fetchAppScoreJson("robokiller");
        setRoboKillerJson(json);
      } catch {
        setRoboKillerJson("Error loading details");
      } finally {
        setRoboKillerLoading(false);
      }
    }
    setRoboKillerVisible(true);
  };

  // Reset state on close (matching MVC: $('#appScoreDisplay > td').remove())
  const handleClose = () => {
    setNomoroboVisible(false);
    setNomoroboJson(null);
    setRoboKillerVisible(false);
    setRoboKillerJson(null);
    onClose();
  };

  if (!show || !phone) return null;

  const nomoroboStatus = getStatusHtml(phone.nomoroboStatus);
  const roboKillerStatus = getStatusHtml(phone.roboKillerStatus);

  return (
    <div
      className="modal fade show d-block modal-lg"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="appScoreModalLabel"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content" style={{ width: 740 }}>
          <div className="modal-header">
            <h3 className="modal-title" id="appScoreModalLabel">
              App Score Status for {phoneNumber}
            </h3>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            />
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th scope="col">Nomorobo Status</th>
                      <th scope="col">RoboKiller Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row 1: Status text + See/Hide Details buttons */}
                    <tr>
                      <td style={{ borderRight: "none", borderBottom: "none" }}>
                        <span style={{ fontWeight: "bold", color: nomoroboStatus.color }}>
                          {nomoroboStatus.text}
                        </span>{" "}
                        <button
                          className="btn btn-info btn-sm text-white"
                          onClick={handleNomoroboToggle}
                          disabled={nomoroboLoading}
                        >
                          {nomoroboLoading
                            ? "Loading..."
                            : nomoroboVisible
                              ? "Hide Details"
                              : "See Details"}
                        </button>
                      </td>
                      <td style={{ borderRight: "none", borderBottom: "none" }}>
                        <span style={{ fontWeight: "bold", color: roboKillerStatus.color }}>
                          {roboKillerStatus.text}
                        </span>{" "}
                        <button
                          className="btn btn-info btn-sm text-white"
                          onClick={handleRoboKillerToggle}
                          disabled={roboKillerLoading}
                        >
                          {roboKillerLoading
                            ? "Loading..."
                            : roboKillerVisible
                              ? "Hide Details"
                              : "See Details"}
                        </button>
                      </td>
                    </tr>
                    {/* Row 2: JSON detail panels (matching MVC #appScoreDisplay row) */}
                    {(nomoroboVisible || roboKillerVisible) && (
                      <tr>
                        <td style={{ width: 250, borderTop: "none", borderRight: "none", borderBottom: "none", verticalAlign: "top", padding: 0 }}>
                          {nomoroboVisible && nomoroboJson && (
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", maxWidth: 700, overflow: "auto", margin: 0 }}>
                              {nomoroboJson}
                            </pre>
                          )}
                        </td>
                        <td style={{ width: 250, borderTop: "none", borderRight: "none", borderBottom: "none", verticalAlign: "top", padding: 0 }}>
                          {roboKillerVisible && roboKillerJson && (
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", maxWidth: 700, overflow: "auto", margin: 0 }}>
                              {roboKillerJson}
                            </pre>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppScoreModal;
